// PZ Document Types and Interfaces (Przyjecie Zewnetrzne - Warehouse Receipt)
import type { TransportRouteSummaryDto } from '../dto/transport';
import type { AllocationUnit } from '../utils/dimensionConversion';
import type { WarehouseBlockedAction } from '../modules/warehouse/blockedActions';

/** Currencies the catalog average purchase cost may be entered in. */
export type AverageCostCurrency = 'PLN' | 'EUR' | 'USD';

export enum PZStatus {
  DRAFT = 'DRAFT',
  CONFIRMED = 'CONFIRMED',
  RECEIVED = 'RECEIVED',
  CANCELLED = 'CANCELLED'
}

export interface PZStockValuation {
  extraTotalPLN: number;
  /** Owner 2026-10-12: THIS receipt's freight (its batches), not the whole ZD's. */
  freightPLN: number | null;
  /** TRUCK = the batch took the freight of the truck it came on; AVERAGED = spread over the ZD's trucks. */
  freightSource?: 'TRUCK' | 'AVERAGED' | null;
  freightTruckLabels?: string[];
  landedPLN: number | null;
  allocationUnit: string | null;
  valuationComplete: boolean;
  issues: string[];
}

export interface PZDocument {
  invoicePending?: boolean;
  purchaseInvoiceId?: string | null;
  /** WP2: the purchase invoice these goods were received on was withdrawn. */
  withdrawnPurchaseInvoice?: { number: string | null } | null;
  id: string;
  pzNumber: string;
  supplierId?: string;
  supplier?: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    address?: string;
    taxId?: string;
  };
  issueDate: string;
  receiptDate: string;
  expectedDeliveryDate?: string;
  supplierName?: string;
  supplierAddress?: string;
  supplierNIP?: string;
  supplierInvoiceNumber?: string;
  supplierInvoiceDate?: string;
  supplierInvoiceAmount?: number;
  supplierInvoiceCurrency?: string;
  recipientName?: string;
  recipientAddress?: string;
  recipientNIP?: string;
  warehouseLocation?: string;
  /**
   * The supplier order (ZD) this receipt was generated from, or null for a
   * manual PZ. Distinct from the internal `sourceSupplierOrderId` trace field
   * that `responseFilter` strips: this one is deliberately exposed so the UI
   * can scope the delivery-transport picker without depending on how the page
   * was navigated to.
   */
  sourceSupplierOrderId?: string | null;
  sourceTransportId?: string | null;
  sourceTransportIds?: string[];
  sourceTransports?: TransportRouteSummaryDto[];
  transportCompany?: string;
  transportInvoiceNo?: string;
  transportCost?: number;
  transportCurrency?: string;
  vehicleRegistration?: string;
  driverName?: string;
  driverPhone?: string;
  totalQuantity?: number;
  totalNetWeight?: number;
  totalGrossWeight?: number;
  totalValue?: number;
  acceptedReceiptCostPLN?: number;
  /** Invoice receipt: value of its batches today (moves with freight edits); purchase-price readers only. */
  currentBatchValuePLN?: number;
  currency: string;
  allocateTransportCost?: boolean;
  allocateCustomsCost?: boolean;
  status: PZStatus;
  approvedBy?: string;
  receivedBy?: string;
  confirmedAt?: string;
  receivedAt?: string;
  notes?: string;
  internalNotes?: string;
  createdById?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: {
    email: string;
    name?: string;
  };
  items?: PZItem[];
  /** Stock purchase only: freight/landed cost already inside the batch value of this receipt. */
  stockValuation?: PZStockValuation | null;
  /** WP1: a PZ from a ZD carries its own freight/customs/other copy that is not in the batch value. */
  ownStockCostsIgnored?: boolean;
  attachments?: PZAttachment[];
  /** Backend-computed reasons an operator action is unavailable (see modules/warehouse/blockedActions.ts). */
  blockedActions?: WarehouseBlockedAction[];
  /**
   * Storno (owner decision 2026-09-18): a RECEIVED PZ can be reversed instead
   * of merely cancelled — every line's received quantity is corrected to zero,
   * status becomes CANCELLED and the document is stamped with these three
   * fields. `stornoAt` is the discriminator between an ordinary cancelled
   * draft (never stamped) and a storno'd receipt: both share
   * `status === CANCELLED`.
   */
  stornoAt?: string | null;
  stornoReason?: string | null;
  stornoByName?: string | null;
}

export interface PZItem {
  acceptedReceiptCostPLN?: number;
  /** Invoice receipt line: value of its batch today (moves with freight edits). */
  currentBatchValuePLN?: number;
  purchaseInvoiceItemId?: string | null;
  id: string;
  pzDocumentId: string;
  /**
   * Internal supplier-order trace id. `responseFilter` strips it from every PZ
   * response, so it is effectively ALWAYS undefined on the frontend — never
   * gate UI on it. Use `supplierOrderTraceLocked` instead.
   */
  sourceSupplierOrderItemId?: string;
  /**
   * True when this line came from a supplier order and its physical product and
   * quantity are therefore fixed. The backend-computed replacement for the
   * stripped trace id above — the reason the lock UI can finally engage before
   * the save is refused rather than after.
   */
  supplierOrderTraceLocked?: boolean;
  productVariantId?: string;
  productName?: string;
  productCode?: string;
  sku?: string;
  ean?: string;
  unit?: string;
  quantityOrdered: number;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  // NULL = not yet entered (backend falls back to quantityOrdered when
  // stocking); explicit 0 stocks zero units. See migration 097.
  quantityReceived: number | null;
  quantityReceivedPcs?: number | null;
  quantityReceivedM2?: number | null;
  quantityReceivedM3?: number | null;
  quantityDamaged?: number;
  quantityDamagedPcs?: number | null;
  quantityDamagedM2?: number | null;
  quantityDamagedM3?: number | null;
  width?: number | null;
  height?: number | null;
  length?: number | null;
  thickness?: number | null;
  netWeight?: number | null;
  grossWeight?: number | null;
  productCategoryName?: string | null;
  productCategoryColor?: string | null;
  productCategoryParentId?: string | null;
  productCategoryParentName?: string | null;
  productCategoryParentColor?: string | null;
  productCategoryDimensionDisplayProfile?: string | null;
  // Optional second presentation/pricing unit. It may point at any other
  // dimensional view because the source line itself can be pcs, m², or m³.
  conversionUnit?: 'pcs' | 'm2' | 'm3' | null;
  conversionQuantity?: number;
  purchaseQuantity?: number | null;
  purchaseUnit?: import('../utils/commercialQuantity').QuantityUnit | null;
  unitPrice?: number;
  totalPrice?: number;
  exchangeRatePLN?: number;
  unitPricePLN?: number;
  totalPricePLN?: number;
  currency: string;
  allocatedTransportCost?: number;
  allocatedCustomsCost?: number;
  allocatedOtherCost?: number;
  totalUnitCost?: number;
  cnCode?: string;
  countryOfOrigin?: string;
  description?: string;
  notes?: string;
  productVariant?: {
    id: string;
    sku: string;
    variantName: string;
    width?: number | null;
    height?: number | null;
    length?: number | null;
    thickness?: number | null;
    netWeight?: number | null;
    grossWeight?: number | null;
    product?: {
      id: string;
      code: string;
      name: string;
      ean?: string | null;
      category?: string;
      productCategoryId?: string | null;
      productCategoryName?: string | null;
      productCategoryColor?: string | null;
      productCategoryParentId?: string | null;
      productCategoryParentName?: string | null;
      productCategoryParentColor?: string | null;
      productCategoryDimensionDisplayProfile?: string | null;
      width?: number | null;
      height?: number | null;
      length?: number | null;
      thickness?: number | null;
    };
  };
}

export interface PZAttachment {
  id: string;
  pzId: string;
  fileName: string;
  filePath: string;
  fileType: string;
  fileSize: number;
  description?: string;
  category?: string;
  uploadedAt: string;
  uploadedBy?: string;
  user?: {
    name: string;
    email: string;
  };
}

export interface CreatePZDto {
  pzNumber?: string;
  issueDate: string;
  receiptDate: string;
  supplierId: string;
  currency: string;
  sourceTransportId?: string | null;
  sourceTransportIds?: string[];
  transportCompany?: string;
  transportInvoiceNo?: string;
  allocateTransportCost?: boolean;
  transportCost?: number;
  transportCurrency?: string;
  vehicleRegistration?: string;
  driverName?: string;
  driverPhone?: string;
  supplierInvoiceNumber?: string;
  supplierInvoiceDate?: string;
  supplierInvoiceAmount?: number;
  supplierInvoiceCurrency?: string;
  approvedBy?: string | null;
  receivedBy?: string | null;
  notes?: string;
  items: {
    id?: string;
    productVariantId: string;
    unit: AllocationUnit;
    quantityOrdered: number;
    quantityReceived?: number | null;
    width?: number | null;
    height?: number | null;
    length?: number | null;
    thickness?: number | null;
    netWeight?: number | null;
    grossWeight?: number | null;
    conversionUnit?: 'pcs' | 'm2' | 'm3' | null;
    conversionQuantity?: number;
    purchaseQuantity?: number | null;
    purchaseUnit?: import('../utils/commercialQuantity').QuantityUnit | null;
    unitPrice?: number;
    exchangeRatePLN?: number;
    currency: string;
    ean?: string;
    notes?: string;
  }[];
}

export interface UpdatePZDto extends Partial<CreatePZDto> {
  correctionReason?: string;
  expectedUpdatedAt?: string;
  status?: PZStatus;
}

export interface PZFilters {
  status?: PZStatus[];
  dateRange?: [string | null, string | null];
  search?: string;
  supplierId?: string;
}

// Inventory Types
export interface Inventory {
  pendingIssueCount?: number;
  pendingIssueQuantity?: number | null;
  pendingIssueQuantityUnit?: import('../utils/commercialQuantity').QuantityUnit | null;
  pendingIssueQuantityPcs?: number | null;
  pendingIssueQuantityM2?: number | null;
  pendingIssueQuantityM3?: number | null;
  id: string;
  productVariantId: string;
  quantityUnit?: import('../utils/commercialQuantity').QuantityUnit | null;
  quantityAvailable: number;
  quantityAvailablePcs?: number | null;
  quantityAvailableM2?: number | null;
  quantityAvailableM3?: number | null;
  quantityInTransit: number;
  quantityInTransitPcs?: number | null;
  quantityInTransitM2?: number | null;
  quantityInTransitM3?: number | null;
  quantityReserved: number;
  quantityReservedPcs?: number | null;
  quantityReservedM2?: number | null;
  quantityReservedM3?: number | null;
  quantityTotal: number;
  quantityTotalPcs?: number | null;
  quantityTotalM2?: number | null;
  quantityTotalM3?: number | null;
  averageCostPLN?: number;
  totalValuePLN?: number;
  status: string;
  stockStatus?: 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK';
  lastMovementAt?: string;
  lastMovementType?: string;
  productVariant?: {
    id: string;
    sku: string;
    variantName: string;
    minStockLevelPcs?: number | null;
    minStockLevelM2?: number | null;
    minStockLevelM3?: number | null;
    maxStockLevelPcs?: number | null;
    maxStockLevelM2?: number | null;
    maxStockLevelM3?: number | null;
    product?: {
      id: string;
      code: string;
      name: string;
      width?: number | null;
      length?: number | null;
      height?: number | null;
      thickness?: number | null;
      category?: string;
      productCategoryId?: string | null;
      productCategoryName?: string | null;
      productCategoryColor?: string | null;
      productCategoryParentId?: string | null;
      productCategoryParentName?: string | null;
      productCategoryParentColor?: string | null;
      productCategoryDimensionDisplayProfile?: string | null;
    };
  };
  batches?: InventoryBatch[];
  recentMovements?: InventoryMovement[];
}

export interface InventoryBatch {
  id: string;
  productVariantId: string;
  batchNumber: string;
  pzDocumentId: string;
  quantityUnit?: import('../utils/commercialQuantity').QuantityUnit | null;
  quantityReceived: number;
  quantityReceivedPcs?: number | null;
  quantityReceivedM2?: number | null;
  quantityReceivedM3?: number | null;
  quantityRemaining: number;
  quantityRemainingPcs?: number | null;
  quantityRemainingM2?: number | null;
  quantityRemainingM3?: number | null;
  quantityReserved: number;
  quantityReservedPcs?: number | null;
  quantityReservedM2?: number | null;
  quantityReservedM3?: number | null;
  unitCostPLN?: number;
  totalCostPLN?: number;
  receiptDate: string;
  purchaseDate: string;
  status: 'ACTIVE' | 'DEPLETED' | 'EXPIRED';
  freelyAvailable?: number;
  freelyAvailablePcs?: number | null;
  freelyAvailableM2?: number | null;
  freelyAvailableM3?: number | null;
  pzDocument?: {
    id: string;
    pzNumber: string;
    receiptDate?: string;
  };
}

export interface InventoryMovement {
  id: string;
  productVariantId: string;
  // Matches the DB "MovementType" enum (backend/src/services/initService.js).
  // 'ADJUSTMENT' is legacy, pre-096-fix rows: Inventory.lastMovementType is a
  // TEXT column (not enum-constrained) and InventoryDashboard.tsx still
  // renders it as a case alongside ADJUSTMENT_IN/ADJUSTMENT_OUT.
  movementType: 'PZ_IN' | 'WZ_OUT' | 'ADJUSTMENT_IN' | 'ADJUSTMENT_OUT' | 'DAMAGE' | 'RETURN' | 'TRANSFER' | 'INVENTORY_COUNT' | 'ADJUSTMENT';
  quantityUnit?: import('../utils/commercialQuantity').QuantityUnit | null;
  quantity: number;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  quantityBefore: number;
  quantityBeforePcs?: number | null;
  quantityBeforeM2?: number | null;
  quantityBeforeM3?: number | null;
  quantityAfter: number;
  quantityAfterPcs?: number | null;
  quantityAfterM2?: number | null;
  quantityAfterM3?: number | null;
  unitCostPLN?: number;
  totalCostPLN?: number;
  referenceNumber?: string;
  notes?: string;
  /** WP15: an ADJUSTMENT_OUT that is a supplier-complaint write-off („Odpis reklamacyjny”). */
  reason?: 'COMPLAINT_WRITE_OFF';
  movementDate: string;
  performedBy?: string;
}

export interface Product {
  id: string;
  code: string;
  name: string;
  englishName?: string;
  ean?: string | null;
  description?: string;
  category?: string;
  productCategoryId?: string | null;
  productCategoryName?: string | null;
  productCategoryColor?: string | null;
  productCategoryParentId?: string | null;
  productCategoryParentName?: string | null;
  productCategoryParentColor?: string | null;
  productCategoryDimensionDisplayProfile?: string | null;
  cnCode?: string;
  countryOfOrigin?: string;
  netWeight?: number;
  /** Weight of ONE piece in kg (Karta produktu: waga sztuki). */
  grossWeight?: number;
  width?: number;
  height?: number;
  length?: number;
  thickness?: number;
  /**
   * „Liczymy tylko w sztukach” (migration 196): dimensions are a description
   * only; new lines have a quantity in szt. only and the product counts in no
   * m² / m³ total. Variants inherit it.
   */
  countInPiecesOnly?: boolean;
  defaultWeight?: number;
  averagePurchasePricePLN?: number | null;
  averagePurchasePriceUnit?: 'pcs' | 'm2' | 'm3' | null;
  /** Currency of averagePurchasePricePLN (historical name; migration 189). */
  averagePurchasePriceCurrency?: AverageCostCurrency | null;
  averagePurchasePriceUpdatedAt?: string | null;
  averagePurchasePriceUpdatedByName?: string | null;
  defaultPurchasePrice?: number | null;
  defaultPurchaseCurrency?: string | null;
  defaultSalePrice?: number | null;
  defaultSaleCurrency?: string | null;
  status: 'ACTIVE' | 'INACTIVE' | 'DISCONTINUED';
  isActive: boolean;
  variants?: ProductVariant[];
  createdAt: string;
  updatedAt: string;
}

export interface ProductVariant {
  id: string;
  productId: string;
  sku: string;
  ean?: string;
  variantName: string;
  size?: string;
  color?: string;
  configuration?: string;
  attributes?: Record<string, string>;
  isDefault?: boolean;
  eanOverride?: string | null;
  width?: number;
  height?: number;
  length?: number;
  thickness?: number;
  netWeight?: number;
  grossWeight?: number;
  /** Inherited from the product („Liczymy tylko w sztukach”). */
  countInPiecesOnly?: boolean;
  /** Effective average purchase cost (variant override or product value). */
  averagePurchasePricePLN?: number | null;
  /** Real-variant override; null = "jak produkt podstawowy". */
  averagePurchasePricePLNOverride?: number | null;
  averagePurchasePriceUnit?: 'pcs' | 'm2' | 'm3' | null;
  averagePurchasePriceCurrency?: AverageCostCurrency | null;
  weight?: number;
  defaultPurchasePrice?: number;
  defaultPurchaseCurrency?: string;
  defaultSalePrice?: number;
  defaultSaleCurrency?: string;
  defaultPurchasePriceOverride?: number | null;
  defaultPurchaseCurrencyOverride?: string | null;
  defaultSalePriceOverride?: number | null;
  defaultSaleCurrencyOverride?: string | null;
  basePriceUSD?: number;
  basePricePLN?: number;
  minStockLevelPcs?: number | null;
  minStockLevelM2?: number | null;
  minStockLevelM3?: number | null;
  maxStockLevelPcs?: number | null;
  maxStockLevelM2?: number | null;
  maxStockLevelM3?: number | null;
  reorderPoint?: number;
  isActive: boolean;
  /**
   * Product card (findById) only: the variant's effective logistics set —
   * dimensions and piece weight (owner decisions 2026-09-24, 2026-09-30).
   */
  logistics?: VariantLogistics | null;
  /** True when a real variant has its own set (always, after migration 186). */
  hasOwnLogistics?: boolean;
  product?: Product;
  inventory?: Inventory;
}

export interface VariantLogistics {
  length?: number | null;
  width?: number | null;
  height?: number | null;
  thickness?: number | null;
  grossWeight?: number | null;
}

export interface SupplierContact {
  name?: string;
  phone?: string;
  email?: string;
}

export interface Supplier {
  id: string;
  name: string;
  contactPerson?: string;
  contactPeople?: SupplierContact[] | null;
  email?: string;
  phone?: string;
  address?: string;
  taxId?: string;
  isActive: boolean;
}
